"use client";
import { Api } from "@/Apis";
import DateHelper from "@/Helpers/DateHelper";
import { ApiResult } from "@/Apis/ClinicApi";
import {
  CenterOptionEnumSelect,
  CommonSelect,
  DatePickerJalali,
} from "@/Components";
import { Helper } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import { ChildrenOf } from "@/Helpers/CenterOptions.types";
import { formProps } from "@/Helpers/Constants";
import { useApp, useT } from "@/Hooks";
import useConstants from "@/Hooks/useConstants";
import { Button, Col, Flex, Form, Input, Row, Spin } from "antd";
import { RuleObject } from "antd/es/form";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useState } from "react";

export interface CustomerCenterUserCreateOrEditPageForm {
  Id: number;
  CenterId: number;
  GenderKey: ChildrenOf<"Gender">;
  FirstName: string;
  LastName: string;
  NationalCode: string;
  PhoneNumber: string;
  FamilyRelationshipId: number;
  DateBirth?: Dayjs;
}

export interface CustomerCenterUserCreateOrEditPageProps {
  values?: Partial<CustomerCenterUserCreateOrEditPageForm>;
  callback: (result?: CustomerCenterUserCreateOrEditPageResult) => void;
}
export interface CustomerCenterUserCreateOrEditPageResult {
  Id: number;
}

const CustomerCenterUserCreateOrEditPage: FC<
  CustomerCenterUserCreateOrEditPageProps
> = ({ values, callback }) => {
  const { cancelButtonProps, saveButtonProps } = useConstants();
  const t = useT();
  const id = values?.Id;
  const [form] = Form.useForm<CustomerCenterUserCreateOrEditPageForm>();
  const formCenterId = Form.useWatch("CenterId", form);
  const [loading, setLoading] = useState(!!id);
  const { message } = useApp();
  const onFinish = async (values: CustomerCenterUserCreateOrEditPageForm) => {
    setLoading(true);
    try {
      let r: ApiResult;
      const _values = {
        ...values,
        DateBirth: DateHelper.toDateOnlyOrUndefined(values.DateBirth),
      };
      if (id) r = await Api.CustomerCenterUser.edit(_values);
      else r = await Api.CustomerCenterUser.create(_values);
      if (r.Success) {
        message.savedSuccessfully();
        const Id: number = id ?? r.Result?.Id;
        callback({ Id });
      } else message.errors(r.Errors);
    } catch (error) { }
    setLoading(false);
  };
  useEffect(() => {
    const load = async () => {
      if (id) {
        setLoading(true);
        try {
          let { Result } = await Api.CustomerCenterUser.find({ Id: id });
          if (Result) form.setFieldsValue(Result);
        } catch (error) { }
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const checkPhoneNumberValidator = async (rule: RuleObject, value: any) => {
    if (!Helper.checkPhoneNumber(value))
      throw new Error(
        t(
          "common.message.invalidPhoneNumber",
          undefined,
          "Please enter a valid mobile phone number.",
        ),
      );
  };
  const checkIdentityNumberValidator = async (rule: RuleObject, value: any) => {
    if (!Helper.checkDefaultNationalIdentityNumber(value))
      throw new Error(
        t(
          "customer.centerUser.createOrEdit.invalidNationalCode",
          undefined,
          "Enter a valid identity number.",
        ),
      );
  };
  return (
    <Spin spinning={loading}>
      <Row>
        <Col span={24}>
          <Form
            {...formProps}
            onFinish={onFinish}
            initialValues={{
              ...values,
            }}
            form={form}
          >
            <Form.Item hidden name="Id">
              <Input />
            </Form.Item>
            <Row>
              <Col md={8} xs={24}>
                <Form.Item
                  name="CenterId"
                  label={t("common.field.center", undefined, "Center")}
                  rules={[{ required: true }]}
                >
                  <CommonSelect
                    sourceType="Customer"
                    methodName="Centers"
                    model={{ IsMember: true }}
                    disabled={!!values?.CenterId || !!values?.Id}
                  />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  name="FamilyRelationshipId"
                  label={t(
                    "customer.centerUser.createOrEdit.relation",
                    undefined,
                    "Relation",
                  )}
                  rules={[{ required: true }]}
                >
                  <CommonSelect
                    sourceType="Customer"
                    methodName="CenterOptions"
                    model={{
                      CenterId: formCenterId,
                      ParentKey: CenterOptions.FamilyRelationship.key,
                    }}
                  />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  name="GenderKey"
                  label={t("common.field.gender", undefined, "Gender")}
                  rules={[{ required: true }]}
                >
                  <CenterOptionEnumSelect
                    parentKey={CenterOptions.Enum.Gender.key}
                  />
                </Form.Item>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item
                  name="FirstName"
                  label={t("common.field.name", undefined, "Name")}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col md={12} xs={24}>
                <Form.Item
                  name="LastName"
                  label={t("common.field.lastName", undefined, "Last Name")}
                  rules={[{ required: true }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  name="NationalCode"
                  label={t(
                    "common.field.nationalCode",
                    undefined,
                    "Identity number",
                  )}
                  rules={[
                    { required: true },
                    { validator: checkIdentityNumberValidator },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  name="PhoneNumber"
                  label={t(
                    "common.field.phoneNumber",
                    undefined,
                    "Mobile Phone",
                  )}
                  rules={[
                    { required: true },
                    { validator: checkPhoneNumberValidator },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col md={8} xs={24}>
                <Form.Item
                  name="DateBirth"
                  label={t("common.field.birthDate", undefined, "Birth date")}
                >
                  <DatePickerJalali birthDate />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Flex justify="end" gap={8}>
                  <Button
                    {...cancelButtonProps}
                    onClick={() => callback(undefined)}
                  />
                  <Button {...saveButtonProps}>
                    {t("common.action.save", undefined, "Save")}
                  </Button>
                </Flex>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
    </Spin>
  );
};

export default CustomerCenterUserCreateOrEditPage;
